/**
 * LocationMessage - Component to display location messages in chat
 */

import { useState, useEffect } from 'react'
import { MapPin, Navigation, ExternalLink, Clock } from 'lucide-react'
import { locationService } from '../../services/LocationService'

interface LocationMessageProps {
  location: {
    latitude: number
    longitude: number
    accuracy?: number
    altitude?: number
    speed?: number
    heading?: number
    timestamp: number
  }
  shareType: 'one-time' | 'live' | 'duration'
  duration?: number
  address?: string
  fromSelf?: boolean
}

export function LocationMessage({
  location,
  shareType,
  duration,
  address,
  fromSelf = false
}: LocationMessageProps) {
  const [distance, setDistance] = useState<string | null>(null)
  const [timeAgo, setTimeAgo] = useState<string>('')

  useEffect(() => {
    // Calculate distance if we have our location
    const calculateDistanceFromUser = async () => {
      try {
        const userLocation = await locationService.getCurrentLocation()
        const dist = locationService.calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          location.latitude,
          location.longitude
        )
        setDistance(locationService.formatDistance(dist))
      } catch (error) {
        console.log('Could not calculate distance:', error)
      }
    }

    if (!fromSelf) {
      calculateDistanceFromUser()
    }

    // Update time ago
    const updateTimeAgo = () => {
      const seconds = Math.floor((Date.now() - location.timestamp) / 1000)

      if (seconds < 60) {
        setTimeAgo('just now')
      } else if (seconds < 3600) {
        const mins = Math.floor(seconds / 60)
        setTimeAgo(`${mins} minute${mins > 1 ? 's' : ''} ago`)
      } else if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600)
        setTimeAgo(`${hours} hour${hours > 1 ? 's' : ''} ago`)
      } else {
        const days = Math.floor(seconds / 86400)
        setTimeAgo(`${days} day${days > 1 ? 's' : ''} ago`)
      }
    }

    updateTimeAgo()
    const interval = setInterval(updateTimeAgo, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [location, fromSelf])

  const openInMaps = () => {
    const mapsUrl = `https://maps.google.com/?q=${location.latitude},${location.longitude}`
    window.open(mapsUrl, '_blank', 'noopener,noreferrer')
  }

  const getShareTypeIcon = () => {
    switch (shareType) {
      case 'live':
        return <Navigation className="h-4 w-4 animate-pulse" />
      case 'duration':
        return <Clock className="h-4 w-4" />
      default:
        return <MapPin className="h-4 w-4" />
    }
  }

  const getShareTypeLabel = () => {
    switch (shareType) {
      case 'live':
        return 'Live location'
      case 'duration':
        return `Sharing for ${duration} minutes`
      default:
        return 'Location'
    }
  }

  // Create static map URL (using OpenStreetMap tile server as fallback)
  const mapImageUrl = `https://static-maps.yandex.ru/1.x/?lang=en-US&ll=${location.longitude},${location.latitude}&z=15&l=map&size=300,150&pt=${location.longitude},${location.latitude},pm2rdm`

  return (
    <div className={`rounded-lg border ${
      fromSelf
        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
    } p-3 max-w-sm`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getShareTypeIcon()}
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {getShareTypeLabel()}
          </span>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {timeAgo}
        </span>
      </div>

      {/* Map preview */}
      <div
        className="relative mb-2 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-700 cursor-pointer group"
        onClick={openInMaps}
      >
        <img
          src={mapImageUrl}
          alt="Location map"
          className="w-full h-32 object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white/90 dark:bg-gray-900/90 rounded-full p-3 shadow-lg">
            <MapPin className="h-6 w-6 text-blue-500" />
          </div>
        </div>
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink className="h-4 w-4 text-white" />
        </div>
      </div>

      {/* Location details */}
      <div className="space-y-1">
        {address && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {address}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
          </span>
          {location.accuracy && (
            <span>±{Math.round(location.accuracy)}m</span>
          )}
        </div>

        {distance && !fromSelf && (
          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
            <Navigation className="h-3 w-3" />
            <span>{distance} away</span>
          </div>
        )}

        {location.speed !== null && location.speed !== undefined && location.speed > 0.5 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Moving at {Math.round(location.speed * 3.6)} km/h
          </div>
        )}
      </div>

      {/* Actions */}
      <button
        onClick={openInMaps}
        className="mt-2 w-full py-1.5 px-3 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors flex items-center justify-center gap-2"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Open in Maps
      </button>
    </div>
  )
}